import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User, LoginCredentials, RegisterData, AuthTokens } from '@/types';
import api from '@/api';
import { useGoogleDrive } from '@/composables/useGoogleDrive';
import { usePdfEditorStore } from '@/stores/pdfEditor.store';
import { clearPersistedSession } from '@/constants/storageKeys';

// 2FA verification state
export interface MfaRequiredState {
  requiresMfa: true;
  // Signed, short-lived token from the password step. Sent back to
  // /mfa/verify so the server derives the userId from it (the client
  // never asserts which account is being verified).
  mfaToken: string;
  email: string;
}

export const useAuthStore = defineStore('auth', () => {
  // Initialize token from localStorage synchronously (fixes refresh logout issue)
  const storedToken = localStorage.getItem('accessToken');
  const accessToken = ref<string | null>(storedToken);
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const initialized = ref(false);

  // 2FA state
  const mfaPending = ref<MfaRequiredState | null>(null);

  // Consider authenticated if we have a token (even before user data loads)
  const isAuthenticated = computed(() => !!accessToken.value);

  const initAuth = async () => {
    if (initialized.value) return;

    if (accessToken.value) {
      try {
        await fetchUser();
      } catch (err) {
        // Only drop the session when the server rejected it. A network
        // error or 5xx must not log the user out (the token may be fine).
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 401 || status === 403) {
          logout();
        }
      }
    }
    initialized.value = true;
  };

  const login = async (credentials: LoginCredentials) => {
    loading.value = true;
    error.value = null;
    mfaPending.value = null;

    try {
      const response = await api.post<{ data: (AuthTokens & { user: User }) | MfaRequiredState }>(
        '/auth/login',
        credentials
      );

      const data = response.data.data;

      // Check if 2FA is required
      if ('requiresMfa' in data && data.requiresMfa) {
        mfaPending.value = data;
        return null; // Signal that 2FA is required
      }

      const { accessToken: token, refreshToken, user: userData } = data as AuthTokens & { user: User };

      accessToken.value = token;
      user.value = userData;

      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refreshToken);

      return userData;
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message || 'Login failed';
      error.value = message;
      throw new Error(message);
    } finally {
      loading.value = false;
    }
  };

  const verifyMfa = async (code: string) => {
    if (!mfaPending.value) {
      throw new Error('No pending 2FA verification');
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await api.post<{ data: AuthTokens & { user: User } }>(
        '/auth/mfa/verify',
        {
          mfaToken: mfaPending.value.mfaToken,
          code,
        }
      );

      const { accessToken: token, refreshToken, user: userData } = response.data.data;

      accessToken.value = token;
      user.value = userData;
      mfaPending.value = null;

      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refreshToken);

      return userData;
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message || 'Invalid verification code';
      error.value = message;
      throw new Error(message);
    } finally {
      loading.value = false;
    }
  };

  const cancelMfa = () => {
    mfaPending.value = null;
    error.value = null;
  };

  const register = async (data: RegisterData) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post<{ data: (AuthTokens & { user: User }) | { requiresVerification: true; email: string } }>(
        '/auth/register',
        data
      );

      const responseData = response.data.data;

      // Check if email verification is required
      if ('requiresVerification' in responseData && responseData.requiresVerification) {
        return responseData;
      }

      const { accessToken: token, refreshToken, user: userData } = responseData as AuthTokens & { user: User };

      accessToken.value = token;
      user.value = userData;

      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refreshToken);

      return userData;
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message || 'Registration failed';
      error.value = message;
      throw new Error(message);
    } finally {
      loading.value = false;
    }
  };

  const fetchUser = async () => {
    try {
      const response = await api.get<{ data: User }>('/auth/me');
      user.value = response.data.data;
      return user.value;
    } catch (err) {
      throw err;
    }
  };

  const logout = () => {
    // Revoke the refresh token server-side (fire and forget); clearing
    // localStorage alone leaves it valid until it expires.
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      api.post('/auth/logout', { refreshToken }).catch(() => {
        // Best effort: the local session is cleared regardless.
      });
    }
    mfaPending.value = null;
    error.value = null;
    user.value = null;
    accessToken.value = null;
    // Tokens plus local signing drafts (signer names and emails of this account)
    clearPersistedSession();
    // Reset shared composable state to prevent stale data across accounts
    useGoogleDrive().resetState();
    // The PDF editor holds the open document, its objects and undo history
    usePdfEditorStore().resetEditor();
  };

  const clearError = () => {
    error.value = null;
  };

  // 2FA management functions
  const getMfaStatus = async () => {
    try {
      const response = await api.get<{
        data: {
          enabled: boolean;
          enabledAt: string | null;
          backupCodesRemaining: number;
        };
      }>('/auth/mfa/status');
      return response.data.data;
    } catch (err) {
      throw err;
    }
  };

  const setupMfa = async () => {
    try {
      const response = await api.post<{
        data: {
          qrCode: string;
          secret: string;
          backupCodes: string[];
        };
      }>('/auth/mfa/setup');
      return response.data.data;
    } catch (err) {
      throw err;
    }
  };

  const verifyMfaSetup = async (code: string) => {
    try {
      await api.post('/auth/mfa/verify-setup', { code });
      return true;
    } catch (err) {
      throw err;
    }
  };

  const disableMfa = async (code: string, password: string) => {
    try {
      await api.post('/auth/mfa/disable', { code, password });
      return true;
    } catch (err) {
      throw err;
    }
  };

  const regenerateBackupCodes = async (code: string) => {
    try {
      const response = await api.post<{ data: { backupCodes: string[] } }>(
        '/auth/mfa/backup-codes',
        { code }
      );
      return response.data.data.backupCodes;
    } catch (err) {
      throw err;
    }
  };

  return {
    user,
    accessToken,
    loading,
    error,
    initialized,
    isAuthenticated,
    mfaPending,
    initAuth,
    login,
    verifyMfa,
    cancelMfa,
    register,
    fetchUser,
    logout,
    clearError,
    getMfaStatus,
    setupMfa,
    verifyMfaSetup,
    disableMfa,
    regenerateBackupCodes,
  };
});
